import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const STABLE_ID_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$";
const IDEMPOTENCY_KEY_PATTERN = "^[A-Za-z0-9][A-Za-z0-9._:-]{7,127}$";
const RESERVATION_NUMBER_PATTERN = /^YY-(\d{4})(\d{2})(\d{2})-([0-9A-F]{12})$/u;
const RESERVATION_NUMBER_SOURCE_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._:-]{11,127}$/u;
const FNV_1A_64_OFFSET = 0xcbf29ce484222325n;
const FNV_1A_64_PRIME = 0x100000001b3n;
const LOWER_48_BITS = 0xffffffffffffn;

const StableIdSchema = Type.String({ minLength: 1, maxLength: 128, pattern: STABLE_ID_PATTERN });
const IdempotencyKeySchema = Type.String({ pattern: IDEMPOTENCY_KEY_PATTERN });
const CanonicalDateTimeSchema = Type.String({ minLength: 24, maxLength: 24 });
const NullableDateTimeSchema = Type.Union([CanonicalDateTimeSchema, Type.Null()]);
const ReservationVersionSchema = Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER });
const CustomerNameSchema = Type.String({ minLength: 1, maxLength: 60 });
const CustomerMobileSchema = Type.String({ pattern: "^1[3-9][0-9]{9}$" });
const ReservationReasonSchema = Type.String({ minLength: 2, maxLength: 200 });
const PaginationTokenSchema = Type.String({ minLength: 32, maxLength: 1024 });

export const CUSTOMER_RESERVATION_PAGE_LIMIT_DEFAULT = 50;
export const CUSTOMER_RESERVATION_PAGE_LIMIT_MAX = 100;
export const CUSTOMER_RESERVATION_CALENDAR_DAY_LIMIT_MAX = 1_000;

export const CustomerReservationNumberSchema = Type.String({
  minLength: 24,
  maxLength: 24,
  pattern: "^YY-[0-9]{8}-[0-9A-F]{12}$",
});
export type CustomerReservationNumber = Static<typeof CustomerReservationNumberSchema>;

export const CustomerReservationStateSchema = Type.Union([
  Type.Literal("PENDING_CONFIRMATION"),
  Type.Literal("CONFIRMED"),
  Type.Literal("WAITING"),
  Type.Literal("CALLED"),
  Type.Literal("SEATED"),
  Type.Literal("CANCELLED"),
  Type.Literal("NO_SHOW"),
  Type.Literal("EXPIRED"),
]);
export type CustomerReservationState = Static<typeof CustomerReservationStateSchema>;

export const CustomerReservationTransitionActionSchema = Type.Union([
  Type.Literal("CONFIRM"),
  Type.Literal("ARRIVE"),
  Type.Literal("CALL"),
  Type.Literal("SKIP"),
  Type.Literal("RETRACT_CALL"),
  Type.Literal("CANCEL"),
  Type.Literal("NO_SHOW"),
  Type.Literal("EXPIRE"),
]);
export type CustomerReservationTransitionAction = Static<
  typeof CustomerReservationTransitionActionSchema
>;

export const CreateCustomerReservationRequestSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    customerName: CustomerNameSchema,
    customerMobile: CustomerMobileSchema,
    partySize: Type.Integer({ minimum: 1, maximum: 20 }),
    requestedStartAt: CanonicalDateTimeSchema,
    durationMinutes: Type.Integer({ minimum: 30, maximum: 720, multipleOf: 30 }),
    note: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    depositFen: Type.Literal(0),
    discountFen: Type.Literal(0),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type CreateCustomerReservationRequest = Static<
  typeof CreateCustomerReservationRequestSchema
>;

export const CustomerReservationTransitionRequestSchema = Type.Union([
  Type.Object(
    {
      action: Type.Union([Type.Literal("CONFIRM"), Type.Literal("ARRIVE"), Type.Literal("CALL")]),
      expectedReservationVersion: ReservationVersionSchema,
      idempotencyKey: IdempotencyKeySchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      action: Type.Union([
        Type.Literal("SKIP"),
        Type.Literal("RETRACT_CALL"),
        Type.Literal("CANCEL"),
        Type.Literal("NO_SHOW"),
        Type.Literal("EXPIRE"),
      ]),
      expectedReservationVersion: ReservationVersionSchema,
      reason: ReservationReasonSchema,
      idempotencyKey: IdempotencyKeySchema,
    },
    { additionalProperties: false },
  ),
]);
export type CustomerReservationTransitionRequest = Static<
  typeof CustomerReservationTransitionRequestSchema
>;

export const SeatCustomerReservationRequestSchema = Type.Object(
  {
    expectedReservationVersion: ReservationVersionSchema,
    tableId: StableIdSchema,
    expectedTableRevision: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    idempotencyKey: IdempotencyKeySchema,
  },
  { additionalProperties: false },
);
export type SeatCustomerReservationRequest = Static<typeof SeatCustomerReservationRequestSchema>;

export const CustomerReservationReadModelSchema = Type.Object(
  {
    contractVersion: Type.Literal("1.0.0"),
    reservationId: StableIdSchema,
    reservationNumber: CustomerReservationNumberSchema,
    reservationVersion: ReservationVersionSchema,
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    customerName: CustomerNameSchema,
    customerMobile: CustomerMobileSchema,
    partySize: Type.Integer({ minimum: 1, maximum: 20 }),
    requestedStartAt: CanonicalDateTimeSchema,
    requestedEndAt: CanonicalDateTimeSchema,
    note: Type.Union([Type.String({ minLength: 1, maxLength: 300 }), Type.Null()]),
    depositFen: Type.Literal(0),
    discountFen: Type.Literal(0),
    source: Type.Union([Type.Literal("FRONTDESK"), Type.Literal("SELF_SERVICE")]),
    state: CustomerReservationStateSchema,
    confirmedAt: NullableDateTimeSchema,
    arrivedAt: NullableDateTimeSchema,
    queueEnteredAt: NullableDateTimeSchema,
    calledAt: NullableDateTimeSchema,
    seatedAt: NullableDateTimeSchema,
    cancelledAt: NullableDateTimeSchema,
    noShowAt: NullableDateTimeSchema,
    expiredAt: NullableDateTimeSchema,
    assignedTableId: Type.Union([StableIdSchema, Type.Null()]),
    assignedTableNumber: Type.Union([Type.String({ minLength: 1, maxLength: 32 }), Type.Null()]),
    sessionId: Type.Union([StableIdSchema, Type.Null()]),
    orderId: Type.Union([StableIdSchema, Type.Null()]),
    orderNumber: Type.Union([Type.String({ minLength: 1, maxLength: 128 }), Type.Null()]),
    createdBy: StableIdSchema,
    lastActionBy: StableIdSchema,
    createdAt: CanonicalDateTimeSchema,
    updatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerReservationReadModel = Static<typeof CustomerReservationReadModelSchema>;

export const CustomerReservationWorkspaceItemSchema = Type.Object(
  {
    reservation: CustomerReservationReadModelSchema,
    queuePosition: Type.Union([
      Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    estimatedWaitMinutes: Type.Union([
      Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
      Type.Null(),
    ]),
    waitEstimateKind: Type.Union([
      Type.Literal("NOT_IN_QUEUE"),
      Type.Literal("READY_NOW"),
      Type.Literal("CURRENT_IDLE_TABLES"),
      Type.Literal("UNAVAILABLE_NO_IDLE_TABLE"),
    ]),
    noShowGraceMinutes: Type.Integer({ minimum: 0, maximum: 240 }),
    noShowEligibleAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerReservationWorkspaceItem = Static<
  typeof CustomerReservationWorkspaceItemSchema
>;

export const CustomerReservationWorkspaceSchema = Type.Object(
  {
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    currentBusinessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    items: Type.Array(CustomerReservationWorkspaceItemSchema),
    nextWaitingReservationId: Type.Union([StableIdSchema, Type.Null()]),
    idleTableCount: Type.Integer({ minimum: 0, maximum: 500 }),
    waitEstimateBasis: Type.Literal("CURRENT_IDLE_TABLES_AND_PLANNED_DURATIONS"),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerReservationWorkspace = Static<typeof CustomerReservationWorkspaceSchema>;

export const CustomerReservationWorkspacePageSchema = Type.Object(
  {
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    snapshotId: PaginationTokenSchema,
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    currentBusinessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    items: Type.Array(CustomerReservationWorkspaceItemSchema, {
      maxItems: CUSTOMER_RESERVATION_PAGE_LIMIT_MAX,
    }),
    pageLimit: Type.Integer({ minimum: 1, maximum: CUSTOMER_RESERVATION_PAGE_LIMIT_MAX }),
    totalItems: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    itemsBefore: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    nextCursor: Type.Union([PaginationTokenSchema, Type.Null()]),
    nextWaitingReservationId: Type.Union([StableIdSchema, Type.Null()]),
    idleTableCount: Type.Integer({ minimum: 0, maximum: 500 }),
    waitEstimateBasis: Type.Literal("CURRENT_IDLE_TABLES_AND_PLANNED_DURATIONS"),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerReservationWorkspacePage = Static<
  typeof CustomerReservationWorkspacePageSchema
>;

export const CustomerReservationHistoryPageSchema = Type.Object(
  {
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    snapshotId: PaginationTokenSchema,
    items: Type.Array(CustomerReservationWorkspaceItemSchema, {
      maxItems: CUSTOMER_RESERVATION_PAGE_LIMIT_MAX,
    }),
    pageLimit: Type.Integer({ minimum: 1, maximum: CUSTOMER_RESERVATION_PAGE_LIMIT_MAX }),
    totalItems: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    itemsBefore: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    nextCursor: Type.Union([PaginationTokenSchema, Type.Null()]),
    generatedAt: CanonicalDateTimeSchema,
  },
  { additionalProperties: false },
);
export type CustomerReservationHistoryPage = Static<typeof CustomerReservationHistoryPageSchema>;

export const CustomerReservationCalendarDaySchema = Type.Object(
  {
    tenantId: StableIdSchema,
    storeId: StableIdSchema,
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    timeZone: Type.String({ minLength: 1, maxLength: 80 }),
    rangeStartedAt: CanonicalDateTimeSchema,
    generatedAt: CanonicalDateTimeSchema,
    items: Type.Array(CustomerReservationWorkspaceItemSchema, {
      maxItems: CUSTOMER_RESERVATION_CALENDAR_DAY_LIMIT_MAX,
    }),
  },
  { additionalProperties: false },
);
export type CustomerReservationCalendarDay = Static<typeof CustomerReservationCalendarDaySchema>;

export const CustomerReservationSeatingReceiptSchema = Type.Object(
  {
    reservationId: StableIdSchema,
    reservationNumber: CustomerReservationNumberSchema,
    tableId: StableIdSchema,
    tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
    tableRevision: Type.Integer({ minimum: 1, maximum: Number.MAX_SAFE_INTEGER }),
    sessionId: StableIdSchema,
    orderId: StableIdSchema,
    orderNumber: Type.String({ minLength: 1, maxLength: 128 }),
    businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
    sessionStartedAt: CanonicalDateTimeSchema,
    depositFen: Type.Literal(0),
    discountFen: Type.Literal(0),
  },
  { additionalProperties: false },
);
export type CustomerReservationSeatingReceipt = Static<
  typeof CustomerReservationSeatingReceiptSchema
>;

export const CustomerReservationMutationResultSchema = Type.Object(
  {
    operation: Type.Union([
      Type.Literal("CREATE"),
      CustomerReservationTransitionActionSchema,
      Type.Literal("SEAT"),
    ]),
    reservation: CustomerReservationReadModelSchema,
    seating: Type.Union([CustomerReservationSeatingReceiptSchema, Type.Null()]),
  },
  { additionalProperties: false },
);
export type CustomerReservationMutationResult = Static<
  typeof CustomerReservationMutationResultSchema
>;

export const CustomerReservationBusinessEventPayloadSchema = Type.Object(
  { reservation: CustomerReservationReadModelSchema },
  { additionalProperties: false },
);
export type CustomerReservationBusinessEventPayload = Static<
  typeof CustomerReservationBusinessEventPayloadSchema
>;

function canonicalEpoch(value: string): number | null {
  const epoch = Date.parse(value);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === value ? epoch : null;
}

function isRealBusinessDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(value)) return false;
  const epoch = Date.parse(`${value}T00:00:00.000Z`);
  return Number.isFinite(epoch) && new Date(epoch).toISOString() === `${value}T00:00:00.000Z`;
}

function deriveFortyEightBitHex(value: string): string {
  let hash = FNV_1A_64_OFFSET;
  for (const character of value) {
    hash ^= BigInt(character.codePointAt(0) ?? 0);
    hash = BigInt.asUintN(64, hash * FNV_1A_64_PRIME);
  }
  return (hash & LOWER_48_BITS).toString(16).toUpperCase().padStart(12, "0");
}

export function deriveCustomerReservationNumber(
  businessDate: string,
  reservationId: string,
): CustomerReservationNumber {
  if (!isRealBusinessDate(businessDate)) {
    throw new TypeError("businessDate must be a real calendar date in YYYY-MM-DD format");
  }
  if (
    !RESERVATION_NUMBER_SOURCE_PATTERN.test(reservationId) ||
    [...reservationId].filter((character) => /[A-Za-z0-9]/u.test(character)).length < 12
  ) {
    throw new TypeError("reservationId must contain at least 12 safe alphanumeric characters");
  }
  return `YY-${businessDate.replaceAll("-", "")}-${deriveFortyEightBitHex(
    `${businessDate}\0${reservationId}`,
  )}`;
}

export function isCustomerReservationNumber(value: unknown): value is CustomerReservationNumber {
  if (!Value.Check(CustomerReservationNumberSchema, value)) return false;
  const match = RESERVATION_NUMBER_PATTERN.exec(value);
  return Boolean(match && isRealBusinessDate(`${match[1]}-${match[2]}-${match[3]}`));
}

export function isCreateCustomerReservationRequest(
  value: unknown,
): value is CreateCustomerReservationRequest {
  if (!Value.Check(CreateCustomerReservationRequestSchema, value)) return false;
  const start = canonicalEpoch(value.requestedStartAt);
  return (
    start !== null &&
    value.customerName === value.customerName.trim() &&
    value.note === (value.note === null ? null : value.note.trim())
  );
}

export function isCustomerReservationTransitionRequest(
  value: unknown,
): value is CustomerReservationTransitionRequest {
  if (!Value.Check(CustomerReservationTransitionRequestSchema, value)) return false;
  return !("reason" in value) || value.reason === value.reason.trim();
}

export function isSeatCustomerReservationRequest(
  value: unknown,
): value is SeatCustomerReservationRequest {
  return Value.Check(SeatCustomerReservationRequestSchema, value);
}

export function isCustomerReservationReadModel(
  value: unknown,
): value is CustomerReservationReadModel {
  if (!Value.Check(CustomerReservationReadModelSchema, value)) return false;
  if (!isCustomerReservationNumber(value.reservationNumber)) return false;
  let expectedNumber: CustomerReservationNumber;
  try {
    expectedNumber = deriveCustomerReservationNumber(value.businessDate, value.reservationId);
  } catch {
    return false;
  }
  if (value.reservationNumber !== expectedNumber) return false;
  const createdAt = canonicalEpoch(value.createdAt);
  const updatedAt = canonicalEpoch(value.updatedAt);
  const startAt = canonicalEpoch(value.requestedStartAt);
  const endAt = canonicalEpoch(value.requestedEndAt);
  const rawTimes = [
    value.confirmedAt,
    value.arrivedAt,
    value.queueEnteredAt,
    value.calledAt,
    value.seatedAt,
    value.cancelledAt,
    value.noShowAt,
    value.expiredAt,
  ];
  if (rawTimes.some((time) => time !== null && canonicalEpoch(time) === null)) return false;
  const times = rawTimes.map((time) => (time === null ? null : canonicalEpoch(time)));
  if (
    createdAt === null ||
    updatedAt === null ||
    startAt === null ||
    endAt === null ||
    updatedAt < createdAt ||
    endAt <= startAt ||
    endAt - startAt > 720 * 60_000 ||
    endAt - startAt < 30 * 60_000 ||
    (endAt - startAt) % (30 * 60_000) !== 0
  ) {
    return false;
  }
  const [
    confirmedAt,
    arrivedAt,
    queueEnteredAt,
    calledAt,
    seatedAt,
    cancelledAt,
    noShowAt,
    expiredAt,
  ] = times;
  const noSeatIdentity =
    value.assignedTableId === null &&
    value.assignedTableNumber === null &&
    value.sessionId === null &&
    value.orderId === null &&
    value.orderNumber === null;
  const noTerminal = cancelledAt === null && noShowAt === null && expiredAt === null;
  switch (value.state) {
    case "PENDING_CONFIRMATION":
      return (
        confirmedAt === null &&
        arrivedAt === null &&
        queueEnteredAt === null &&
        calledAt === null &&
        seatedAt === null &&
        noTerminal &&
        noSeatIdentity
      );
    case "CONFIRMED":
      return (
        confirmedAt !== null &&
        arrivedAt === null &&
        queueEnteredAt === null &&
        calledAt === null &&
        seatedAt === null &&
        noTerminal &&
        noSeatIdentity
      );
    case "WAITING":
      return (
        confirmedAt !== null &&
        arrivedAt !== null &&
        queueEnteredAt !== null &&
        calledAt === null &&
        seatedAt === null &&
        noTerminal &&
        noSeatIdentity
      );
    case "CALLED":
      return (
        confirmedAt !== null &&
        arrivedAt !== null &&
        queueEnteredAt !== null &&
        calledAt !== null &&
        seatedAt === null &&
        noTerminal &&
        noSeatIdentity
      );
    case "SEATED":
      return (
        confirmedAt !== null &&
        arrivedAt !== null &&
        queueEnteredAt !== null &&
        calledAt !== null &&
        seatedAt !== null &&
        noTerminal &&
        value.assignedTableId !== null &&
        value.assignedTableNumber !== null &&
        value.sessionId !== null &&
        value.orderId !== null &&
        value.orderNumber !== null
      );
    case "CANCELLED":
      return (
        seatedAt === null &&
        cancelledAt !== null &&
        noShowAt === null &&
        expiredAt === null &&
        noSeatIdentity
      );
    case "NO_SHOW":
      return (
        confirmedAt !== null &&
        arrivedAt === null &&
        queueEnteredAt === null &&
        calledAt === null &&
        seatedAt === null &&
        cancelledAt === null &&
        noShowAt !== null &&
        expiredAt === null &&
        noSeatIdentity
      );
    case "EXPIRED":
      return (
        arrivedAt === null &&
        queueEnteredAt === null &&
        calledAt === null &&
        seatedAt === null &&
        cancelledAt === null &&
        noShowAt === null &&
        expiredAt !== null &&
        noSeatIdentity
      );
  }
}

function isActiveCustomerReservationState(state: CustomerReservationState): boolean {
  return ["PENDING_CONFIRMATION", "CONFIRMED", "WAITING", "CALLED"].includes(state);
}

function isTerminalCustomerReservationState(state: CustomerReservationState): boolean {
  return ["SEATED", "CANCELLED", "NO_SHOW", "EXPIRED"].includes(state);
}

function isCustomerReservationWorkspaceItem(value: CustomerReservationWorkspaceItem): boolean {
  if (!isCustomerReservationReadModel(value.reservation)) return false;
  const noShowEligibleAt = canonicalEpoch(value.noShowEligibleAt);
  const requestedStartAt = canonicalEpoch(value.reservation.requestedStartAt);
  return (
    noShowEligibleAt !== null &&
    requestedStartAt !== null &&
    noShowEligibleAt === requestedStartAt + value.noShowGraceMinutes * 60_000
  );
}

function isCustomerReservationCalendarItem(value: CustomerReservationWorkspaceItem): boolean {
  if (!isCustomerReservationWorkspaceItem(value)) return false;
  const queued = value.reservation.state === "WAITING" || value.reservation.state === "CALLED";
  if (!queued) {
    return (
      value.queuePosition === null &&
      value.estimatedWaitMinutes === null &&
      value.waitEstimateKind === "NOT_IN_QUEUE"
    );
  }
  if (value.queuePosition === null) return false;
  switch (value.waitEstimateKind) {
    case "READY_NOW":
      return value.estimatedWaitMinutes === 0;
    case "CURRENT_IDLE_TABLES":
      return value.estimatedWaitMinutes !== null && value.estimatedWaitMinutes > 0;
    case "UNAVAILABLE_NO_IDLE_TABLE":
      return value.estimatedWaitMinutes === null;
    case "NOT_IN_QUEUE":
      return false;
  }
}

function customerReservationItemsMatchScope(
  items: readonly CustomerReservationWorkspaceItem[],
  tenantId: string,
  storeId: string,
): boolean {
  return (
    items.every(
      (item) =>
        isCustomerReservationWorkspaceItem(item) &&
        item.reservation.tenantId === tenantId &&
        item.reservation.storeId === storeId,
    ) && new Set(items.map((item) => item.reservation.reservationId)).size === items.length
  );
}

function isReservationPageShapeValid(
  items: readonly CustomerReservationWorkspaceItem[],
  pageLimit: number,
  totalItems: number,
  itemsBefore: number,
  nextCursor: string | null,
): boolean {
  if (itemsBefore > totalItems || itemsBefore + items.length > totalItems) return false;
  if (totalItems === 0) {
    return itemsBefore === 0 && items.length === 0 && nextCursor === null;
  }
  if (items.length === 0) return false;
  const consumedItems = itemsBefore + items.length;
  return nextCursor === null
    ? consumedItems === totalItems
    : items.length === pageLimit && consumedItems < totalItems;
}

function isValidReservationTimeZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat("zh-CN", { timeZone }).format(new Date(0));
  } catch {
    return false;
  }
  return true;
}

export function isCustomerReservationWorkspacePage(
  value: unknown,
): value is CustomerReservationWorkspacePage {
  if (!Value.Check(CustomerReservationWorkspacePageSchema, value)) return false;
  if (
    canonicalEpoch(value.generatedAt) === null ||
    !isRealBusinessDate(value.currentBusinessDate) ||
    !isValidReservationTimeZone(value.timeZone) ||
    !isReservationPageShapeValid(
      value.items,
      value.pageLimit,
      value.totalItems,
      value.itemsBefore,
      value.nextCursor,
    ) ||
    !customerReservationItemsMatchScope(value.items, value.tenantId, value.storeId) ||
    !value.items.every((item) => isActiveCustomerReservationState(item.reservation.state))
  ) {
    return false;
  }
  for (const item of value.items) {
    const queued = item.reservation.state === "WAITING" || item.reservation.state === "CALLED";
    if (queued !== (item.queuePosition !== null)) return false;
    if (
      !queued &&
      (item.estimatedWaitMinutes !== null || item.waitEstimateKind !== "NOT_IN_QUEUE")
    ) {
      return false;
    }
    if (
      queued &&
      value.idleTableCount === 0 &&
      (item.estimatedWaitMinutes !== null || item.waitEstimateKind !== "UNAVAILABLE_NO_IDLE_TABLE")
    ) {
      return false;
    }
    if (
      queued &&
      value.idleTableCount > 0 &&
      (item.estimatedWaitMinutes === null ||
        !["READY_NOW", "CURRENT_IDLE_TABLES"].includes(item.waitEstimateKind))
    ) {
      return false;
    }
  }
  const firstQueued = value.items.find((item) => item.queuePosition === 1);
  return (
    firstQueued === undefined ||
    firstQueued.reservation.reservationId === value.nextWaitingReservationId
  );
}

export function isCustomerReservationHistoryPage(
  value: unknown,
): value is CustomerReservationHistoryPage {
  if (!Value.Check(CustomerReservationHistoryPageSchema, value)) return false;
  return (
    canonicalEpoch(value.generatedAt) !== null &&
    isReservationPageShapeValid(
      value.items,
      value.pageLimit,
      value.totalItems,
      value.itemsBefore,
      value.nextCursor,
    ) &&
    customerReservationItemsMatchScope(value.items, value.tenantId, value.storeId) &&
    value.items.every(
      (item) =>
        isTerminalCustomerReservationState(item.reservation.state) &&
        item.queuePosition === null &&
        item.estimatedWaitMinutes === null &&
        item.waitEstimateKind === "NOT_IN_QUEUE",
    )
  );
}

export function isCustomerReservationCalendarDay(
  value: unknown,
): value is CustomerReservationCalendarDay {
  if (!Value.Check(CustomerReservationCalendarDaySchema, value)) return false;
  const rangeStartedAt = canonicalEpoch(value.rangeStartedAt);
  const generatedAt = canonicalEpoch(value.generatedAt);
  if (
    rangeStartedAt === null ||
    generatedAt === null ||
    generatedAt < rangeStartedAt ||
    !isRealBusinessDate(value.businessDate) ||
    !isValidReservationTimeZone(value.timeZone) ||
    !customerReservationItemsMatchScope(value.items, value.tenantId, value.storeId) ||
    !value.items.every(
      (item) =>
        item.reservation.businessDate === value.businessDate &&
        isCustomerReservationCalendarItem(item),
    )
  ) {
    return false;
  }
  return value.items.every((item, index) => {
    const previous = value.items[index - 1];
    if (previous === undefined) return true;
    if (previous.reservation.requestedStartAt !== item.reservation.requestedStartAt) {
      return previous.reservation.requestedStartAt < item.reservation.requestedStartAt;
    }
    return previous.reservation.reservationId < item.reservation.reservationId;
  });
}

export function isCustomerReservationWorkspace(
  value: unknown,
): value is CustomerReservationWorkspace {
  if (!Value.Check(CustomerReservationWorkspaceSchema, value)) return false;
  if (
    canonicalEpoch(value.generatedAt) === null ||
    !isRealBusinessDate(value.currentBusinessDate) ||
    !isValidReservationTimeZone(value.timeZone) ||
    !customerReservationItemsMatchScope(value.items, value.tenantId, value.storeId) ||
    !value.items.every((item) => isActiveCustomerReservationState(item.reservation.state))
  ) {
    return false;
  }
  const queued = value.items.filter((item) =>
    ["WAITING", "CALLED"].includes(item.reservation.state),
  );
  const positions = queued.map((item) => item.queuePosition);
  if (positions.some((position) => position === null)) return false;
  if (new Set(positions).size !== positions.length) return false;
  if (!positions.every((position, index) => position === index + 1)) return false;
  if (
    value.items.some(
      (item) =>
        !["WAITING", "CALLED"].includes(item.reservation.state) && item.queuePosition !== null,
    )
  ) {
    return false;
  }
  for (const item of value.items) {
    if (item.queuePosition === null) {
      if (item.estimatedWaitMinutes !== null || item.waitEstimateKind !== "NOT_IN_QUEUE") {
        return false;
      }
    }
  }
  if (value.idleTableCount === 0) {
    for (const item of queued) {
      if (
        item.estimatedWaitMinutes !== null ||
        item.waitEstimateKind !== "UNAVAILABLE_NO_IDLE_TABLE"
      ) {
        return false;
      }
    }
  } else if (queued.length > 0) {
    const laneAvailabilityMinutes = Array.from(
      { length: Math.min(value.idleTableCount, queued.length) },
      () => 0,
    );
    for (const item of queued) {
      let nextLane = 0;
      for (let index = 1; index < laneAvailabilityMinutes.length; index += 1) {
        const candidateMinutes = laneAvailabilityMinutes[index];
        const currentMinutes = laneAvailabilityMinutes[nextLane];
        if (candidateMinutes === undefined || currentMinutes === undefined) return false;
        if (candidateMinutes < currentMinutes) nextLane = index;
      }
      const expectedWaitMinutes = laneAvailabilityMinutes[nextLane];
      if (expectedWaitMinutes === undefined) return false;
      const plannedDurationMinutes =
        (Date.parse(item.reservation.requestedEndAt) -
          Date.parse(item.reservation.requestedStartAt)) /
        60_000;
      if (
        item.estimatedWaitMinutes !== expectedWaitMinutes ||
        item.waitEstimateKind !== (expectedWaitMinutes === 0 ? "READY_NOW" : "CURRENT_IDLE_TABLES")
      ) {
        return false;
      }
      laneAvailabilityMinutes[nextLane] = expectedWaitMinutes + plannedDurationMinutes;
    }
  }
  return value.nextWaitingReservationId === (queued[0]?.reservation.reservationId ?? null);
}

export function isCustomerReservationMutationResult(
  value: unknown,
): value is CustomerReservationMutationResult {
  if (!Value.Check(CustomerReservationMutationResultSchema, value)) return false;
  if (!isCustomerReservationReadModel(value.reservation)) return false;
  if (value.operation !== "SEAT") return value.seating === null;
  return (
    value.reservation.state === "SEATED" &&
    value.seating !== null &&
    value.seating.reservationId === value.reservation.reservationId &&
    value.seating.reservationNumber === value.reservation.reservationNumber &&
    value.seating.tableId === value.reservation.assignedTableId &&
    value.seating.tableNumber === value.reservation.assignedTableNumber &&
    value.seating.sessionId === value.reservation.sessionId &&
    value.seating.orderId === value.reservation.orderId &&
    value.seating.orderNumber === value.reservation.orderNumber &&
    canonicalEpoch(value.seating.sessionStartedAt) !== null
  );
}

export function isCustomerReservationBusinessEventPayload(
  value: unknown,
): value is CustomerReservationBusinessEventPayload {
  return (
    Value.Check(CustomerReservationBusinessEventPayloadSchema, value) &&
    isCustomerReservationReadModel(value.reservation)
  );
}
